import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import '@dji/vc-player/dist/theme.min.css';
import LinkPreview from '@/components/LinkPreview';
import PhotoPreview, { IImgSize } from '@/components/PhotoPreview';
import { ThreadTagType } from '@/components/ThreadTag/mIndex';
import ThreadTitle from '@/components/ThreadTitle/mIndex';
import UserInfo from '@/components/UserInfo/mIndex';
import CommonIcon from '../CommonIcon';
import ThreadRelatedInfo from '@/components/ThreadRelatedInfo/mIndex';
import { ContentTypeEnum } from '@/api/thread';
import { IAuhtor } from '@/api/detail';
import { getVideoInfo } from './config';
import Styles from './index.module.scss';
import { StoreContext, namespace } from '@/context';
import { ThreadDetailCtx } from '@/context/threadDetail';
import useSls, { MetricTypes  } from '@/hooks/useSls';
import { IFirstFrameInfo } from './types';
import { sanitizePostHtml } from '@/utils/html';

interface IThreadDetail {
  className?: string;
  author: IAuhtor;
  subject: string;
  tags?: ThreadTagType[];
  contents: any;
  relatedInfo: {
    views: number;
    comments: number;
    dateline: number;
    devices: any[]; // TODO
    subjects: any[];
    channel_tags: any[];
  };
  isPrivate?: boolean;
  special: number;
  richTextCont: any;
  onClickUserAction?: () => void;
}

const ThreadDetail = ({
  special,
  richTextCont,
  className = '',
  author,
  subject,
  tags = [],
  contents = [],
  relatedInfo,
  isPrivate = false,
  onClickUserAction = () => {},
}: IThreadDetail) => {
  const { reportSlsData } = useSls();
  const firstFrameReportedRef = useRef<Record<string, boolean>>({});

  const [VCPlayer, setVCPlayer] = useState<any>();
  const t = useTranslations();

  const { state } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};
  const threadDetail = useContext(ThreadDetailCtx);

  const [pcWidth, setPcWidth] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const importVCPlayer = async () => {
    const vcPlayer = (await import('@dji/vc-player')).default;
    setVCPlayer(vcPlayer);
  };

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
    };

    window.addEventListener('resize', handleResize);

    // 清理事件监听器
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useMemo(() => {
    if (windowWidth > 1279) {
      setPcWidth(864); // 设计稿容器宽度
    } else {
      setPcWidth(676); // 设计稿容器宽度
    }
  }, [windowWidth])

  useEffect(() => {
    importVCPlayer();
    const options = {
      gallery: '#detail-gallery',
      children: "a",
      pswpModule: () => import('photoswipe')
    };
    let lightbox:any = new PhotoSwipeLightbox(options);
    lightbox.init();
    const detailGallery = document.getElementById('detail-gallery');
    const addInlineLinkClickEvent = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.matches('span[data-type="inline-link"]')) {
        const url = event.target.textContent;
        if (url) {
          window.open(url, '_blank');
        }
      }
    }
    detailGallery?.addEventListener('click', (e) => addInlineLinkClickEvent(e));
    return () => {
      lightbox.destroy();
      lightbox = null;
      detailGallery?.removeEventListener('click', (e) => addInlineLinkClickEvent(e));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickUserAction = () => {
    onClickUserAction();
  };

  // 处理播放器首帧事件
  const handleFirstFrame = (firstFrameInfo: IFirstFrameInfo) => {
    const { firstFrameTime, url: videoUrl, duration } = firstFrameInfo
    reportSlsData([
      ['reportMetric', MetricTypes.firstFrameLoadTime, firstFrameTime],
      ['setEnvironment',{
        [MetricTypes.videoUrl] : videoUrl,
        [MetricTypes.videoDuration]: duration
      }],
    ])
    if (!videoUrl || firstFrameReportedRef.current[videoUrl]) return;
    const firstFrameTimeMs = firstFrameTime * 1000;  

    track?.(
      TRACK_EVENTS?.[namespace.detail].threadVideoFirstFrame.name,
      {
        video_url: videoUrl,
        first_frame_time: firstFrameTimeMs,
        is_first_frame_slow: firstFrameTimeMs > 5000,  // 超过 5s 认为慢
        video_duration: duration,
        thread_tid: threadDetail?.tid,
      },
      namespace.detail,
    );

    firstFrameReportedRef.current[videoUrl] = true;
  }

  return (
    <div className={cx(Styles.root, className)}>
      {
        isPrivate && <div className={Styles.auditTips}>{t('tips_thread_auditing')}</div>
      }
      <ThreadTitle className={Styles.title} title={subject} tags={tags} isShowToolTip />
      <UserInfo className={Styles.userInfo} {...author} author={author}
        handleClickAvatarCbk={() => {
          // 用户主页入口埋点 (pc端)
          track?.(TRACK_EVENTS?.[namespace.detail].userProfileEntryClick.name, {
            thread_tid: threadDetail?.tid,
            thread_name: threadDetail?.subject,
            account_slug: threadDetail?.author?.id,
            list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
            position: 'Author',
          }, namespace.detail);
      }}/>
      {
        special == 6 ? (
          <div className={Styles.contents} id="detail-gallery">
            {
              (contents || []).map((cont: any) => {
                if (cont.type == ContentTypeEnum.text && !!cont.desc) {
                  return (
                    <div
                      key={cont.key}
                      className={Styles.text}
                      dangerouslySetInnerHTML={{ __html: sanitizePostHtml(cont.desc) }}
                    />
                  );
                } else if (cont.type == ContentTypeEnum.photo) {
                  return (
                    <PhotoPreview
                      key={cont.key}
                      id={cont.key}
                      className={cx(Styles.photo, 'pc-detail-photo')}
                      url={cont?.multi_size?.large || cont.url}
                      previewUrl={cont?.multi_size?.extra_large || cont.url}
                      ratio={cont.desc}
                      onRemove={() => {}}
                      width={pcWidth}
                    />
                  );
                } else if (cont.type == ContentTypeEnum.video) {
                  const { vid, isOut } = getVideoInfo(cont?.url);
                  return VCPlayer ? (
                    <div key={cont.key} className={Styles.videoPreviewWrapper}>
                      <VCPlayer
                        key={cont.key}
                        appId="dji-bbs"
                        vid={vid}
                        debugOptions={
                          ['dev', 'test'].includes(process.env.ENV as string)
                            ? { requestUrl: 'https://vip.aasky.net' }
                            : {}
                        }
                        onFirstFrame={(firstFrameInfo: IFirstFrameInfo) => {
                          handleFirstFrame(firstFrameInfo)
                        }}
                      />
                    </div>
                  ) : <div key={cont.key}>Loading</div>;
                } else if (cont.type == ContentTypeEnum.iframeLink) {
                  return (
                    <LinkPreview
                      key={cont.key}
                      id={cont.key}
                      className={Styles.link}
                      link={cont.url}
                      title={cont.desc}
                      onRemove={() => {}}
                    />
                  )
                } else if (cont.type == ContentTypeEnum.customLink) {
                  return (
                    <div
                      key={cont.key}
                      onClick={() => window.open(cont.url)}
                      className={Styles.customLink}
                    >
                      <CommonIcon icon="link" className={Styles.customLinkIcon} />
                      {cont.desc || cont.url}
                    </div>
                  )
                }
              })
            }
            <ThreadRelatedInfo className={Styles.relatedInfo} {...relatedInfo} onClickUserAction={handleClickUserAction} isMobile={false} />
          </div>
        ) : (
          <div className={Styles.richTextWrapper}>
            <div className={Styles.richText} dangerouslySetInnerHTML={richTextCont} />
            <ThreadRelatedInfo className={Styles.relatedInfo} {...relatedInfo} onClickUserAction={handleClickUserAction} isMobile={false} />
          </div>
        )
      }
    </div>
  )
};

export default ThreadDetail;
